import { type FC, useEffect, useState } from 'react'
import { Controller, useFieldArray, useWatch } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon, PlusIcon, Trash01Icon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useExperienceShellFormContext } from '@/features/experience-shell/hooks/use-experience-shell-form-context.ts'
import { checkIsItineraryLocked } from '@/features/experience-shell/utils.ts'
import { useTranslation } from '@/translations/i18n'
import { useChangeStep } from '../hooks/use-change-step'
import { type Experience, StepKeyName } from '../types'
import {
	ColumnFields,
	Content,
	Fieldset,
	Footer,
	InputPrice,
	YesNoField
} from './atoms'

export const DISCOUNT_MIN_VALUE = 5
export const DISCOUNT_MAX_VALUE = 50
// Default price limit (in USD)
export const DEFAULT_PRICE_LIMIT = 999_999_999
export const MAX_PUBLIC_GROUP_SIZE = 100

export const PricingDiscountStep: FC<{
	experienceId: number
	itineraries: Experience['itineraries']
	isUpgrade: boolean
}> = ({ experienceId, itineraries, isUpgrade }) => {
	const { t } = useTranslation()
	const [hasLimitedTimeDiscount, setHasLimitedTimeDiscount] = useState<
		boolean | undefined
	>(undefined)
	const {
		control,
		register,
		formState: { errors },
		setValue
	} = useExperienceShellFormContext(isUpgrade)
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'pricing_discounts.discounts'
	})
	const changeStep = useChangeStep({
		experienceId,
		currentStep: StepKeyName.enum.pricing_discounts,
		isUpgrade
	})
	const perInstancePrivateGroupWatch = useWatch({
		control,
		name: 'pricing_discounts.instance_private_groups'
	})
	const newListingDiscountWatch = useWatch({
		control,
		name: 'pricing_discounts.limitedTimeDiscount'
	})
	const maximumPublicGroupSizeWatch = useWatch({
		control,
		name: 'pricing_discounts.maximum_public_group_size'
	})

	useEffect(() => {
		if (newListingDiscountWatch !== undefined) {
			setHasLimitedTimeDiscount(!!newListingDiscountWatch)
		}
	}, [newListingDiscountWatch])

	return (
		<>
			<Content title={t('experience_shell:step_pricing_discount')}>
				<div className='flex flex-col gap-6'>
					<ColumnFields>
						<InputText
							errorMessage={
								errors.pricing_discounts
									?.maximum_public_group_size
									? t(
											'errors:maximum_public_group_size_error',
											{ count: MAX_PUBLIC_GROUP_SIZE }
										)
									: undefined
							}
							label={t(
								'experience_shell:maximum_public_group_size'
							)}
							max={MAX_PUBLIC_GROUP_SIZE}
							min={1}
							onKeyDown={(evt) =>
								['e', 'E', '+', '-', '.', ','].includes(
									evt.key
								) && evt.preventDefault()
							}
							step='1'
							type='number'
							{...register(
								'pricing_discounts.maximum_public_group_size',
								{
									setValueAs: (value) =>
										value === '' ||
										value === 0 ||
										value === null ||
										value === undefined
											? undefined
											: Number.parseFloat(value)
								}
							)}
						/>
					</ColumnFields>
					<Separator orientation='horizontal' />
					<Fieldset title={t('experience_shell:individual_rate')}>
						<ColumnFields>
							<InputPrice
								errorMessage={
									errors.pricing_discounts
										?.guest_individual_price
										? t('experience_shell:price_error')
										: undefined
								}
								label={t('experience_shell:each_guest_pays')}
								max={DEFAULT_PRICE_LIMIT}
								min={1}
								onKeyDown={(evt) =>
									['e', 'E', '+', '-', '.', ','].includes(
										evt.key
									) && evt.preventDefault()
								}
								step={'1'}
								{...register(
									'pricing_discounts.guest_individual_price',
									{
										setValueAs: (value) =>
											value === '' ||
											value === null ||
											value === undefined
												? undefined
												: Number.parseFloat(value)
									}
								)}
							/>
						</ColumnFields>
					</Fieldset>
					<Separator orientation='horizontal' />
					<div className='flex w-full flex-col gap-4'>
						<Controller
							control={control}
							name='pricing_discounts.instance_private_groups'
							render={({ field }) => (
								<div className='flex w-full flex-col gap-2'>
									<YesNoField
										labelTitle={
											<b>
												{t(
													'experience_shell:per_instance_private_groups'
												)}
											</b>
										}
										name={field.name}
										onChange={(value) => {
											field.onChange(value)
											if (!value) {
												setValue(
													'pricing_discounts.group_minimum_price',
													undefined
												)
											}
										}}
										value={field.value}
									/>
									<InputHelpText
										error={
											errors.pricing_discounts
												?.instance_private_groups
												? t('errors:field_required')
												: undefined
										}
									/>
								</div>
							)}
						/>
						{perInstancePrivateGroupWatch && (
							<ColumnFields>
								<InputPrice
									errorMessage={
										errors.pricing_discounts
											?.group_minimum_price
											? t('experience_shell:price_error')
											: undefined
									}
									label={t('experience_shell:minimum_price')}
									max={DEFAULT_PRICE_LIMIT}
									min={1}
									onKeyDown={(evt) =>
										['e', 'E', '+', '-', '.', ','].includes(
											evt.key
										) && evt.preventDefault()
									}
									step={'1'}
									{...register(
										'pricing_discounts.group_minimum_price',
										{
											setValueAs: (value) =>
												value === '' ||
												value === null ||
												value === undefined
													? undefined
													: Number.parseFloat(value)
										}
									)}
								/>
							</ColumnFields>
						)}
					</div>
					<Separator orientation='horizontal' />
					<div className='flex w-full flex-col gap-4'>
						<YesNoField
							labelDescription={t(
								'experience_shell:limited_time_description'
							)}
							labelTitle={
								<b>{t('experience_shell:limited_time')}</b>
							}
							name={'limited_time'}
							onChange={(value) => {
								setHasLimitedTimeDiscount(value)
								if (!value) {
									setValue(
										'pricing_discounts.limitedTimeDiscount',
										undefined
									)
								}
							}}
							value={hasLimitedTimeDiscount}
						/>

						{hasLimitedTimeDiscount && (
							<InputText
								{...register(
									'pricing_discounts.limitedTimeDiscount',
									{
										setValueAs: (value) =>
											value === '' ||
											value === 0 ||
											value === null ||
											value === undefined
												? undefined
												: Number.parseFloat(value)
									}
								)}
								errorMessage={
									errors.pricing_discounts
										?.limitedTimeDiscount
										? t(
												'experience_shell:limited_time_discount_error',
												{
													minDiscount:
														DISCOUNT_MIN_VALUE,
													maxDiscount:
														DISCOUNT_MAX_VALUE
												}
											)
										: undefined
								}
								inputSize='sm'
								label={t('experience_shell:discount')}
								step='1'
								type='number'
								typeIcon={
									<div className='flex h-full items-center justify-center border-neutral-300 border-r pr-3 text-neutral-600'>
										%
									</div>
								}
							/>
						)}
					</div>
					<Separator orientation='horizontal' />
					<Controller
						control={control}
						name='pricing_discounts.early_bird_discount'
						render={({ field }) => (
							<div className='flex w-full flex-col gap-2'>
								<YesNoField
									labelDescription={t(
										'experience_shell:20_off_guests'
									)}
									labelTitle={
										<b>
											{t(
												'experience_shell:early_bird_discount'
											)}
										</b>
									}
									name={field.name}
									onChange={field.onChange}
									value={field.value}
								/>
								<InputHelpText
									error={
										errors.pricing_discounts
											?.early_bird_discount
											? t('errors:field_required')
											: undefined
									}
								/>
							</div>
						)}
					/>
					<Separator orientation='horizontal' />
					<div className='flex w-full flex-col gap-4'>
						<Controller
							control={control}
							name='pricing_discounts.offer_large_group_discount'
							render={({ field }) => (
								<div className='flex w-full flex-col gap-2'>
									<YesNoField
										labelDescription={t(
											'experience_shell:attract_larger_groups'
										)}
										labelTitle={
											<b>
												{t(
													'experience_shell:large_group_discount'
												)}
											</b>
										}
										onChange={(value) => {
											field.onChange(value)
											value
												? append({
														groupSize: undefined,
														discountInPercentage:
															undefined
													})
												: remove()
										}}
										value={field.value}
									/>
									{errors.pricing_discounts
										?.offer_large_group_discount && (
										<InputHelpText
											error={t('errors:field_required')}
										/>
									)}
								</div>
							)}
						/>
						{fields.length >= 1 && (
							<div className='flex w-full flex-col gap-4'>
								{fields.map((field, index) => (
									<div
										className='flex w-full flex-col gap-2'
										key={field.id}
									>
										<div className='grid w-full grid-cols-[1fr_1fr_40px] items-end gap-4'>
											<InputText
												inputSize='sm'
												label={t(
													'experience_shell:minimum_number_guests'
												)}
												type='text'
												{...register(
													`pricing_discounts.discounts.${index}.groupSize`
												)}
											/>
											<InputText
												{...register(
													`pricing_discounts.discounts.${index}.discountInPercentage`
												)}
												inputSize='sm'
												label={t(
													'experience_shell:discount'
												)}
												type='text'
												typeIcon={
													<div className='flex h-full items-center justify-center border-neutral-300 border-r pr-3 text-neutral-600'>
														%
													</div>
												}
											/>
											{fields.length >= 2 && (
												<ButtonLegacy
													onClick={() =>
														remove(index)
													}
													shape='square'
													type='button'
													variant='tertiary'
												>
													<Trash01Icon />
												</ButtonLegacy>
											)}
										</div>
										{Array.isArray(
											errors.pricing_discounts?.discounts
										) &&
											!!errors.pricing_discounts
												.discounts[index] && (
												<div className='grid w-full grid-cols-[1fr_1fr_40px] items-end gap-4'>
													{errors.pricing_discounts
														.discounts[index]
														?.groupSize ? (
														<InputHelpText
															error={t(
																'experience_shell:large_group_discount_error',
																{
																	maxPublicGroupSize:
																		maximumPublicGroupSizeWatch
																}
															)}
														/>
													) : (
														<span />
													)}
													{errors.pricing_discounts
														.discounts[index]
														?.discountInPercentage && (
														<InputHelpText
															error={t(
																'errors:percentage_error'
															)}
														/>
													)}
												</div>
											)}
									</div>
								))}
								<ButtonLegacy
									onClick={() =>
										append({
											groupSize: undefined,
											discountInPercentage: undefined
										})
									}
									size='sm'
									type='button'
									variant='tertiary'
								>
									<PlusIcon />
									{t(
										'experience_shell:add_large_group_discount'
									)}
								</ButtonLegacy>
							</div>
						)}
					</div>
				</div>
			</Content>
			<Footer>
				<ButtonLegacy
					disabled={
						changeStep.isBackLoading || changeStep.isContinueLoading
					}
					onClick={() =>
						changeStep.handleChangeStep({
							isBack: true,
							stepToGoTo: checkIsItineraryLocked(itineraries)
								? StepKeyName.enum.photos
								: StepKeyName.enum.itinerary
						})
					}
					type='button'
					variant='tertiary'
				>
					{t('common:back')}
					{changeStep.isBackLoading && <Spinner />}
				</ButtonLegacy>
				<ButtonLegacy
					disabled={
						changeStep.isContinueLoading || changeStep.isBackLoading
					}
					onClick={() =>
						changeStep.handleChangeStep({
							stepToGoTo: StepKeyName.enum.legal
						})
					}
					type='button'
				>
					{t('common:continue')}
					{changeStep.isContinueLoading ? (
						<Spinner />
					) : (
						<ArrowRightIcon />
					)}
				</ButtonLegacy>
			</Footer>
		</>
	)
}
